/**
 * Decisions about the chat's local SEED turn (source card + dupes grid the
 * host page fetches client-side), kept out of `BrainChat.tsx` so they can be
 * tested without rendering an 11k-line component.
 *
 * ── The production failure these exist for ───────────────────────────────
 *
 * Thread `sTHP1X4xORWgDdPe` (side panel, result hash `YrGCYhFhd`): the seed's
 * `/api/dupes` request started (its Google Lens leg ran 17:14:15→17:14:25 UTC)
 * but never completed — no impressions, none of the post-fan-out logs the
 * identical search wrote 37s later inside the brain turn. The web fetcher
 * gives up at 30s and the seed query has `retry: false`, so the error was
 * final. `useResultSeedDisplay` then dropped the error, and the seed turn
 * rendered "Ask me anything about it…" — the copy for a search that FOUND
 * NOTHING. Five seconds later the shopper typed "where are the results?".
 */

/**
 * The seed's search FAILED, as opposed to finding nothing.
 *
 * ⚠️ `!matchesLoading` is load-bearing: TanStack keeps `error` set while a
 * retry is in flight, so without it the turn would read "failed" over the
 * skeleton of the very retry the shopper just asked for.
 */
export function seedProductsFailed({
  matchCount,
  error,
  matchesLoading,
}: {
  matchCount: number
  error: unknown
  matchesLoading: boolean
}): boolean {
  return matchCount === 0 && !!error && !matchesLoading
}

/*
 * ⚠️ `syncSeedIntoMessages` lived here and has been REVERTED.
 *
 * It patched a seed turn with no rows even after the shopper had typed, so a
 * search landing late could still reach the grid. The logic was sound and its
 * unit tests passed — but reproducing the incident against it changed nothing
 * observable: typed at 1.2s, and 35s later the seed turn was still empty and
 * still reading "I'm pulling up similar products…", while the brain turn below
 * had found 31 results and claimed they were "already below the product card
 * in the grid".
 *
 * So there is very likely a SECOND freeze upstream — typing navigates to the
 * thread URL, so `seedDisplay` may stop updating at all, and patching
 * `messages` cannot help if nothing is feeding it. Reverted rather than shipped
 * as a fix that does not fix, with the freeze documented at its real site in
 * `BrainChat.tsx`. Chase the navigation first.
 */
